import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styles from './DENTOverview.module.scss';
import DENTMouth, { MouthView } from '../Mouth/DENTMouth';
import { Mouth } from '../../Interfaces/Mouth.interface';
import ToggleButtons from '../../../../Components/ToggleButtons/ToggleButtons';
import { ToggleButtonView } from '../../../../Enums/ToggleButtonView.enum';
import { Scale } from '../../../Base/Interfaces/Scale.interface';
import DENTModalOverviewViewOptions from '../DENTModalOverviewViewOptions/DENTModalOverviewViewOptions';
import { TOOTH_IMAGE_BASE } from '../../Helpers/periodontalLines/toothImageBase';
import { Tooth } from '../../../../graphql/graphql';

interface Props {
	data: Mouth;
	onToothClick: (tooth: Tooth) => void;
}

interface MouthViewOption {
	value: MouthView;
	label: string;
}

const MOUTH_VIEW_OPTIONS: MouthViewOption[] = [
	{
		value: MouthView.UPPER_JAW,
		label: 'component.dentOverview.upperJaw',
	},
	{
		value: MouthView.FULL_MOUTH,
		label: 'component.dentOverview.fullMouth',
	},
	{
		value: MouthView.LOWER_JAW,
		label: 'component.dentOverview.lowerJaw',
	},
];

const DENTOverview: React.FC<Props> = ({ data, onToothClick }: Props) => {
	const [selectedOption, setSelectedOption] = useState<MouthViewOption>(
		MOUTH_VIEW_OPTIONS[1],
	);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const [scale, setScale] = useState<Scale>({ horizontal: 1, vertical: 1 });

	const calculateScale = () => {
		if (!containerRef.current) return;
		const container = containerRef.current;
		const containerWidth = container.clientWidth;
		const containerHeight = container.clientHeight;

		const toothW = TOOTH_IMAGE_BASE.w;
		const toothH = TOOTH_IMAGE_BASE.h;
		const gap = 20;

		const contentWidth = 8 * toothW;
		const contentHeight =
			selectedOption.value === MouthView.FULL_MOUTH
				? 2 * (2 * toothH + gap) + gap + 20
				: 2000;

		setScale({
			horizontal: contentWidth / containerWidth,
			vertical: contentHeight / containerHeight,
		});
	};

	useLayoutEffect(() => {
		calculateScale();
	}, [containerRef, selectedOption.value]);

	useEffect(() => {
		window.addEventListener('resize', calculateScale);
		return () => window.removeEventListener('resize', calculateScale);
	}, [selectedOption.value]);

	useEffect(() => {
		const el = containerRef.current;
		if (!el) {
			return;
		}

		const observer = new ResizeObserver(calculateScale);
		observer.observe(el);

		return () => observer.disconnect();
	}, [selectedOption.value]);

	return (
		<div className={styles.wrapper}>
			<div ref={containerRef} className={styles.content}>
				<DENTMouth
					data={data}
					view={selectedOption.value}
					scale={scale}
					onToothClick={onToothClick}
				/>
			</div>
			<div className={styles.controls}>
				<ToggleButtons<MouthViewOption>
					options={MOUTH_VIEW_OPTIONS}
					selectedOption={selectedOption}
					onChange={setSelectedOption}
					view={ToggleButtonView.VERTICAL}
				/>
				<DENTModalOverviewViewOptions />
			</div>
		</div>
	);
};

export default DENTOverview;
